/**
 * CombatResultUI - Displays the result of a combat encounter.
 *
 * Responsibilities:
 * - Display win, loss, tie, and timeout combat outcomes
 * - Show life and gold consequences for each result type
 * - React to CombatResult messages from the authoritative backend
 * - Provide render data for UI binding
 * - Reset when a new combat starts or phase changes
 *
 * The backend is authoritative; this component only renders result data
 * that the server sends via CombatResult messages.
 */

import { AppShell, AppShellEvent } from '../AppShell';
import { CombatResult } from '../../shared/messages/MessageContracts';

// ─── Result Type ────────────────────────────────────────────────────────────

/**
 * The type of combat result.
 */
export type CombatResultType = 'win' | 'loss' | 'tie' | 'timeout';

// ─── Combat Result Render Data ──────────────────────────────────────────────

/**
 * Render data for the combat result UI.
 */
export interface CombatResultRenderData {
  /** Whether the result UI should be visible (result has been received). */
  isVisible: boolean;

  /** Whether the component is currently active (combat phase). */
  isActive: boolean;

  /** The type of combat result. */
  resultType: CombatResultType | null;

  /** Human-readable result label. */
  resultLabel: string;

  /** Whether the current player won the combat. */
  isWin: boolean;

  /** Whether the current player lost the combat. */
  isLoss: boolean;

  /** Whether the combat ended in a tie. */
  isTie: boolean;

  /** Whether the combat ended by timeout. */
  isTimeout: boolean;

  /** Life lost by the current player (0 for win). */
  lifeLost: number;

  /** Gold gained by the current player. */
  goldGained: number;

  /** Life lost by the opponent (0 for loss). */
  opponentLifeLost: number;

  /** Gold gained by the opponent. */
  opponentGoldGained: number;

  /** The raw CombatResult message from the server. */
  rawResult: CombatResult | null;

  /** Match ID for the combat that ended. */
  matchId: string | null;

  /** Winner player ID (null for tie/timeout). */
  winnerId: string | null;

  /** Loser player ID (null for tie/timeout). */
  loserId: string | null;
}

/**
 * Callback invoked when render data changes.
 */
export type CombatResultRenderCallback = (data: CombatResultRenderData) => void;

/**
 * Callback invoked when a combat result is received.
 */
export type CombatResultCallback = (result: CombatResult) => void;

// ─── Combat Result UI ───────────────────────────────────────────────────────

export class CombatResultUI {
  private _appShell: AppShell;
  private _playerId: string;
  private _renderCallbacks: CombatResultRenderCallback[] = [];
  private _resultCallbacks: CombatResultCallback[] = [];
  private _eventUnsubscribe: (() => void) | null = null;
  private _isVisible: boolean = false;

  // Internal state
  private _combatActive: boolean = false;
  private _lastResult: CombatResult | null = null;
  private _lastProcessedResultKey: string = '';

  constructor(appShell: AppShell) {
    this._appShell = appShell;
    this._playerId = appShell.playerId;
  }

  // ─── Public Accessors ────────────────────────────────────────────────────

  /** Returns the current render data. */
  get renderData(): CombatResultRenderData {
    return this._buildRenderData();
  }

  /** Returns true if the result UI is currently visible/active. */
  get isVisible(): boolean {
    return this._isVisible;
  }

  /** Returns true if combat is active and awaiting a result. */
  get isActive(): boolean {
    return this._combatActive;
  }

  /** Returns the current result type, or null if no result yet. */
  get resultType(): CombatResultType | null {
    return this._buildRenderData().resultType;
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Show the result UI and start listening for combat events.
   */
  show(): void {
    if (this._isVisible) {
      return;
    }

    this._isVisible = true;

    this._eventUnsubscribe = this._appShell.onEvent(
      this._onAppEvent.bind(this)
    );

    this._notifyRenderCallbacks();
  }

  /**
   * Hide the result UI and stop listening for events.
   */
  hide(): void {
    if (!this._isVisible) {
      return;
    }

    this._isVisible = false;

    if (this._eventUnsubscribe) {
      this._eventUnsubscribe();
      this._eventUnsubscribe = null;
    }
  }

  /**
   * Reset all result state (called when combat ends or phase changes).
   */
  reset(): void {
    this._combatActive = false;
    this._lastResult = null;
    this._lastProcessedResultKey = '';
  }

  // ─── Render Callbacks ────────────────────────────────────────────────────

  /**
   * Subscribe to render data changes.
   */
  onRenderChange(callback: CombatResultRenderCallback): () => void {
    this._renderCallbacks.push(callback);
    return () => {
      const index = this._renderCallbacks.indexOf(callback);
      if (index !== -1) {
        this._renderCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to combat result events.
   */
  onResult(callback: CombatResultCallback): () => void {
    this._resultCallbacks.push(callback);
    return () => {
      const index = this._resultCallbacks.indexOf(callback);
      if (index !== -1) {
        this._resultCallbacks.splice(index, 1);
      }
    };
  }

  // ─── Manual Result Setting ──────────────────────────────────────────────

  /**
   * Manually set a combat result (for testing or direct API use).
   */
  setResult(result: CombatResult): void {
    this._lastResult = result;
    this._notifyResultCallbacks(result);
    this._notifyRenderCallbacks();
  }

  // ─── Internal Event Handling ─────────────────────────────────────────────

  private _onAppEvent(event: AppShellEvent): void {
    switch (event.type) {
      case 'stateChange': {
        const state = event.data as { match: any };
        const matchState = state.match;

        // Detect combat start — always clear previous result when new combat begins
        if (matchState.combatStarted) {
          this._combatActive = true;
          this._lastResult = null;
        }

        // Process combat result
        if (matchState.lastCombatResult && this._combatActive) {
          const resultKey = `${matchState.lastCombatResult.matchId}-${matchState.lastCombatResult.winnerId ?? 'null'}`;
          if (resultKey !== this._lastProcessedResultKey) {
            this._lastProcessedResultKey = resultKey;
            this._handleCombatResult(matchState.lastCombatResult);
          }
        }

        // Reset when phase returns to preparation or match ends
        if (matchState.phase === 'preparation' || matchState.gameOver) {
          this.reset();
        }

        this._notifyRenderCallbacks();
        break;
      }
      case 'screenChange': {
        const data = event.data as { previousScreen: string; currentScreen: string };
        if (data.currentScreen !== 'match' && this._isVisible) {
          this.hide();
        }
        break;
      }
    }
  }

  private _handleCombatResult(result: CombatResult): void {
    this._lastResult = result;
    this._notifyResultCallbacks(result);
  }

  // ─── Internal ────────────────────────────────────────────────────────────

  private _notifyRenderCallbacks(): void {
    const data = this._buildRenderData();
    for (const callback of this._renderCallbacks) {
      callback(data);
    }
  }

  private _notifyResultCallbacks(result: CombatResult): void {
    for (const callback of this._resultCallbacks) {
      callback(result);
    }
  }

  private _buildRenderData(): CombatResultRenderData {
    const result = this._lastResult;

    if (!result) {
      return this._buildEmptyRenderData();
    }

    // Determine result type
    const resultType = this._determineResultType(result);
    const isWin = resultType === 'win';
    const isLoss = resultType === 'loss';
    const isTie = resultType === 'tie';
    const isTimeout = resultType === 'timeout';

    // Human-readable label
    const resultLabel = this._getResultLabel(resultType);

    // Life and gold consequences for current player
    const lifeLost = result.lifeLost[this._playerId] ?? 0;
    const goldGained = result.goldGained[this._playerId] ?? 0;

    // Opponent consequences
    const opponentId = this._getOpponentId(result);
    const opponentLifeLost = opponentId ? (result.lifeLost[opponentId] ?? 0) : 0;
    const opponentGoldGained = opponentId ? (result.goldGained[opponentId] ?? 0) : 0;

    return {
      isVisible: true,
      isActive: this._combatActive,
      resultType,
      resultLabel,
      isWin,
      isLoss,
      isTie,
      isTimeout,
      lifeLost,
      goldGained,
      opponentLifeLost,
      opponentGoldGained,
      rawResult: result,
      matchId: result.matchId,
      winnerId: result.winnerId,
      loserId: result.loserId,
    };
  }

  private _buildEmptyRenderData(): CombatResultRenderData {
    return {
      isVisible: false,
      isActive: this._combatActive,
      resultType: null,
      resultLabel: '',
      isWin: false,
      isLoss: false,
      isTie: false,
      isTimeout: false,
      lifeLost: 0,
      goldGained: 0,
      opponentLifeLost: 0,
      opponentGoldGained: 0,
      rawResult: null,
      matchId: null,
      winnerId: null,
      loserId: null,
    };
  }

  private _determineResultType(result: CombatResult): CombatResultType {
    if (result.isTimeout) {
      return 'timeout';
    }
    if (result.isTie) {
      return 'tie';
    }
    // COOP: the team fights as ONE side — the pair is (team seat, bot), so a
    // WIN/LOSS for the seat is a win/loss for BOTH members. Without this the
    // teammate is neither winnerId nor loserId and falls into the 'loss'
    // default (the "Defeated popup on a team win" visual bug).
    const coop = this._appShell.gameState.match.coop;
    if (
      coop && coop.mode !== 'normal' &&
      (this._playerId === coop.hostId || this._playerId === coop.teammateId)
    ) {
      if (
        result.winnerId !== null &&
        (result.winnerId === coop.hostId || result.winnerId === coop.teammateId)
      ) {
        return 'win';
      }
      if (
        result.loserId !== null &&
        (result.loserId === coop.hostId || result.loserId === coop.teammateId)
      ) {
        return 'loss';
      }
    }
    if (result.winnerId === this._playerId) {
      return 'win';
    }
    if (result.loserId === this._playerId) {
      return 'loss';
    }
    // If neither winner nor loser is the current player, treat as loss
    // (shouldn't happen in normal play, but provides a safe default)
    return 'loss';
  }

  private _getResultLabel(resultType: CombatResultType | null): string {
    switch (resultType) {
      case 'win':
        return 'Victory!';
      case 'loss':
        return 'Defeat';
      case 'tie':
        return 'Tie';
      case 'timeout':
        return 'Timeout';
      default:
        return '';
    }
  }

  private _getOpponentId(result: CombatResult): string | null {
    if (result.winnerId && result.winnerId !== this._playerId) {
      return result.winnerId;
    }
    if (result.loserId && result.loserId !== this._playerId) {
      return result.loserId;
    }
    // For tie/timeout, try to find the other player from lifeLost/goldGained
    const playerIds = Object.keys(result.lifeLost);
    for (const id of playerIds) {
      if (id !== this._playerId) {
        return id;
      }
    }
    return null;
  }
}
